import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { clearApplicationDetails, toggleFetchData } from '../../features/application/applicationDetailsSlice';
import { themeBackgrounds } from '../../theme/theme';
import { setIsFirstAppCreated } from '../../features/auth/authSlice';
import { encryptResponse } from '../../services';

interface Products {
  chat: boolean;
  calls: boolean;
  scheduledCalls: boolean;
  chatBot: boolean;
}

interface CreateApplicationProps {
  open?: boolean;
  onClose?: () => void;
}

interface CreateApplicationResponse {
  status: number;
  message?: string;
  data?: any;
}

export default function CreateApplication({ open = true, onClose }: CreateApplicationProps): React.JSX.Element {
  const [appName, setAppName] = useState<string>('');
  const dispatch = useDispatch();
  const [region, setRegion] = useState<string>('');
  const [products, setProducts] = useState<Products>({
    chat: true,
    calls: true,
    scheduledCalls: true,
    chatBot: true,
  });
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDataState = useSelector((state: any) => state.applicationDetails.is_fetch_data);

  const navigate = useNavigate();
  const handleClose = (): void => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (isValid) {
      const payload = {
        request_id: "notegtest1",
        timestamp: new Date().toISOString(),
        action: "create_application",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_name: appName,
          region: region,
          is_chats_enabled: products.chat,
          is_calls_enabled: products.calls,
          is_scheduled_calls_enabled: products.scheduledCalls,
          is_chat_bot_enabled: products.chatBot,
        }, REQ_ENC_KEY)
      };

      try {
        const response = await httpService.post<CreateApplicationResponse>(`${API_BASE_URL}/user/createApplication`, payload);
        if (response.data?.status === 200 && response.data?.data) {
          dispatch(clearApplicationDetails());
          dispatch(toggleFetchData(!applicationDataState));
          if (userDetails && !userDetails.is_first_app_created) {
            const payload = {
              data: await encryptResponse({
                organization_id: userDetails?.organization_id || '',
                process_data: {
                  is_first_app_created: true
                }
              }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserProcess`, payload);
            if (response.data?.status === 200) {
              dispatch(setIsFirstAppCreated(true));
            }
          }
          if (onClose) {
            onClose();
          } else {
            navigate(-1);
          }
        } else {
          console.log("Failed to create application:", response.data?.message);
        }
      } catch (error) {
        console.log("Error creating application:", error);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setProducts({ ...products, [event.target.name]: event.target.checked });
  };

  const handleRegionChange = (event: SelectChangeEvent<string>): void => {
    setRegion(event.target.value);
  };

  const isValid: boolean = Boolean(appName && region);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create Application
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Application name"
          placeholder="Enter application name"
          fullWidth
          value={appName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAppName(e.target.value)}
          sx={{ mb: 3 }}
        />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Product</Typography>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={products.chat} onChange={handleChange} name="chat" />}
            label="Chat"
          />
          <FormControlLabel
            control={<Checkbox checked={products.calls} onChange={handleChange} name="calls" />}
            label="In App Calls (Audio/Video)"
          />
          <FormControlLabel
            control={<Checkbox checked={products.scheduledCalls} onChange={handleChange} name="scheduledCalls" />}
            label="Scheduled Consultations"
          />
          <FormControlLabel
            control={<Checkbox checked={products.chatBot} onChange={handleChange} name="chatBot" />}
            label="Chat Bot"
          />
        </FormGroup>
        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel>Region</InputLabel>
          <Select value={region} onChange={handleRegionChange} label="Region">
            <MenuItem value="India">India</MenuItem>
            <MenuItem value="US">United States</MenuItem>
            <MenuItem value="Europe">Europe</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
          ⚠️ Choose a region that best represents your users. Once set, changing regions isn't allowed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!isValid}
          sx={{
            background: (!isValid) ? 'grey' : themeBackgrounds.default,
            color: '#ffffff',
            '&:hover': {
              background: themeBackgrounds.default,
              opacity: 0.9,
            },
          }}
          onClick={handleCreate}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
